import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useDecomissionProductsMutation,
  useGetWarehouseProductsForSelectionMutation,
} from '@app/api/Products';
import type {
  ModelProduct,
  ModelProductDecomission,
  ModelProductsDecomission,
} from '@app/api/Products';
import { WAREHOUSE_OPERATIONS_PATH } from '@app/constants';
import { useWarehouseOperationData } from '@app/pages/Warehouses/WarehouseOperations/components/ProductOperation/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';
import { clog } from '@app/utils/clog';

const { useForm, useWatch } = Form;

export const useGoodsWriteOff = () => {
  const {
    ActiveBranchId,
    products,
    warehousesOptions,
    suppliersOptions,
    isPageLoading,
    errors,
  } = useWarehouseOperationData();

  const [isOpenAddAvailableProductsModal, setIsOpenAddAvailableProductsModal] =
    useState(false);
  const onOpenAddAvailableProductsModal = useCallback(() => {
    setIsOpenAddAvailableProductsModal(true);
  }, []);
  const onCloseAddAvailableProductsModal = useCallback(() => {
    setIsOpenAddAvailableProductsModal(false);
  }, []);

  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [form] = useForm<ModelProductsDecomission>();
  const { setFieldValue } = form;
  const formValues = useWatch([], form);
  const [selectedProducts, setSelectedProducts] = useState<ModelProduct[]>([]);

  const [decomissionProducts, { isLoading: isLoadingDecomissionProducts }] =
    useDecomissionProductsMutation();

  const [
    getWarehouseProductsForSelection,
    {
      isLoading: isLoadingWarehouseProducts,
      isUninitialized: isUninitializedWarehouseProducts,
      data: warehouseProducts = [],
    },
  ] = useGetWarehouseProductsForSelectionMutation();

  useErrorHandler(errors);

  const isProductsLoading = useMemo(
    () => isLoadingWarehouseProducts || isUninitializedWarehouseProducts,
    [isLoadingWarehouseProducts, isUninitializedWarehouseProducts]
  );

  const WarehouseId = useMemo(
    () => formValues?.WarehouseId || null,
    [formValues]
  );

  const getWarehouseProductsForSelectionHandler = useCallback(async () => {
    if (!WarehouseId) return;
    try {
      await getWarehouseProductsForSelection({
        data: {
          WarehouseId,
          SearchTerm: null,
          ProductIds: null,
          SupplierIds: null,
          PriceOfSaleS: null,
          PriceOfSaleE: null,
        },
        ActiveBranchId,
      }).unwrap();
    } catch (error) {
      clog(error);
      showNotification(error, 'warning');
    }
  }, [
    getWarehouseProductsForSelection,
    showNotification,
    WarehouseId,
    ActiveBranchId,
  ]);

  const onFinish = useCallback(
    async (values: ModelProductsDecomission) => {
      try {
        await decomissionProducts({ data: values, ActiveBranchId }).unwrap();
        showNotification('Товары успешно списаны.', 'success');
        form.resetFields();
        setSelectedProducts([]);
        navigate(WAREHOUSE_OPERATIONS_PATH);
      } catch (error) {
        clog(error);
        showNotification(error, 'warning');
      }
    },
    [decomissionProducts, form, showNotification, ActiveBranchId, navigate]
  );

  const onAdd = useCallback(
    (selected: ModelProduct[]) => {
      const products: ModelProductDecomission[] = warehouseProducts
        .filter((p) => selected.some((sp) => sp.Id === p.ProductId))
        .map((p) => ({
          ProductId: p.ProductId,
          SupplierId: p.SupplierId,
          PriceOfSale: p.PriceOfSale,
          Count: p.CountAvailable,
        }));
      setFieldValue('Products', products);
      setSelectedProducts(selected);
      onCloseAddAvailableProductsModal();
    },
    [onCloseAddAvailableProductsModal, setFieldValue, warehouseProducts]
  );

  const totalCount = useMemo(() => {
    const list = formValues?.Products ?? [];
    return list.reduce((acc, cur) => acc + Number(cur?.Count ?? 0), 0);
  }, [formValues]);

  const onDelete = useCallback((productId: number) => {
    setSelectedProducts((prev) => prev.filter((p) => p.Id !== productId));
  }, []);

  return {
    form,
    onFinish,
    isPageLoading,
    getWarehouseProductsForSelectionHandler,
    warehousesOptions,
    warehouseProducts,
    isOpenAddAvailableProductsModal,
    onCloseAddAvailableProductsModal,
    onOpenAddAvailableProductsModal,
    onAdd,
    suppliersOptions,
    selectedProducts,
    products,
    formValues,
    totalCount,
    onDelete,
    setSelectedProducts,
    isProductsLoading,
    isLoadingDecomissionProducts,
  };
};
